import {
  useErrorHandlingMutation,
  useErrorHandlingSubscription,
} from '@backoffice-frontend/data-fetching'
import { FccHubReturnRequestStatus } from '@backoffice-frontend/graphql/subscription'
import {
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { useUserHasAccess } from '@backoffice-frontend/restricted'
import { BoumRight } from '@moia-dev/moia-token-claims'
import { useMachine } from '@xstate/react'
import { createContext, type ReactNode, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { assign, setup } from 'xstate'
import { useAdVehicleStream } from '../AdVehicleStream/useAdVehicleStream'
import { useAdVehicleStreamRequestHubReturnMutation } from './graphql/HubReturnMutation.hook'
import { useAdVehicleStreamHubReturnProgressSubscription } from './graphql/HubReturnProgress.subscription.hook'
import type { AdVehicleStreamHubReturnVehicleFragment } from './graphql/HubReturnVehicle.hook'
import {
  getHubReturnErrorMessage,
  type HubReturnFailureReason,
} from './hubReturnErrorMessage'

export type HubReturnActiveCard = 'hubReturn' | null

const AWAITING_RESULT_TIMEOUT_MS = 30_000

type HubReturnValue = {
  activeCard: HubReturnActiveCard
  state: HubReturnMachineState
  isExecuting: boolean
  requestHubReturn: () => void
  confirmHubReturn: (hubId: string) => void
  abandon: () => void
}

export const HubReturnContext = createContext<HubReturnValue | undefined>(
  undefined,
)

type HubReturnProviderProps = {
  vehicle: AdVehicleStreamHubReturnVehicleFragment
  children: ReactNode
}

type MachineContext = {
  activeCard: HubReturnActiveCard
  hubId?: string
  failureReason?: HubReturnFailureReason
}

export const hubReturnMachine = setup({
  types: {
    events: {} as
      | { type: 'SHOW_CARD' }
      | { type: 'EXECUTE'; hubId: string }
      | { type: 'MUTATION_SUCCESS' }
      | { type: 'MUTATION_ERROR' }
      | { type: 'PROGRESS_COMPLETED' }
      | { type: 'PROGRESS_FAILED'; reason?: HubReturnFailureReason }
      | { type: 'ABANDON' },
    context: {} as MachineContext,
  },
  actions: {
    onSuccess: () => {},
    onError: () => {},
  },
  delays: {
    AWAITING_RESULT_TIMEOUT_MS,
  },
}).createMachine({
  initial: 'idle',
  context: {
    activeCard: null,
    hubId: undefined,
    failureReason: undefined,
  },
  states: {
    idle: {
      entry: assign(() => ({
        activeCard: null as HubReturnActiveCard,
        hubId: undefined,
        failureReason: undefined,
      })),
      on: {
        SHOW_CARD: {
          target: 'showingCard',
          actions: assign(() => ({
            activeCard: 'hubReturn' as const,
          })),
        },
      },
    },
    showingCard: {
      on: {
        EXECUTE: {
          target: 'executing',
          actions: assign(({ event }) => ({
            hubId: event.hubId,
          })),
        },
        ABANDON: 'idle',
      },
    },
    executing: {
      on: {
        MUTATION_SUCCESS: 'awaitingResult',
        MUTATION_ERROR: 'failed',
        ABANDON: 'idle',
      },
    },
    awaitingResult: {
      after: {
        AWAITING_RESULT_TIMEOUT_MS: {
          target: 'failed',
          actions: assign(() => ({ failureReason: 'timeout' as const })),
        },
      },
      on: {
        PROGRESS_COMPLETED: 'completed',
        PROGRESS_FAILED: {
          target: 'failed',
          actions: assign(({ event }) => ({ failureReason: event.reason })),
        },
        ABANDON: 'idle',
      },
    },
    completed: {
      entry: 'onSuccess',
      always: 'idle',
    },
    failed: {
      entry: 'onError',
      on: {
        EXECUTE: {
          target: 'executing',
          actions: assign(({ event }) => ({
            hubId: event.hubId,
            failureReason: undefined,
          })),
        },
        ABANDON: 'idle',
      },
    },
  },
})

export type HubReturnMachineState = ReturnType<
  typeof useMachine<typeof hubReturnMachine>
>[0]

export const HubReturnProvider = ({
  vehicle,
  children,
}: HubReturnProviderProps) => {
  const { t } = useTranslation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification(false)
  const { isHandledByCurrentUser } = useAdVehicleStream()

  const [requestHubReturnMutation] = useErrorHandlingMutation(
    useAdVehicleStreamRequestHubReturnMutation,
    { onError: () => undefined },
  )

  const [state, send] = useMachine(
    hubReturnMachine.provide({
      actions: {
        onSuccess: () => {
          enqueueMoiaSuccessNotification(
            t('adFleetMap_toast_hubReturnSuccessful_caption'),
            { duration: 4000 },
          )
        },
        onError: ({ context }) => {
          const { header, copy } = getHubReturnErrorMessage(
            context.failureReason,
            t,
          )
          enqueueMoiaErrorNotification(copy, { title: header })
        },
      },
    }),
  )

  const stateRef = useRef(state)
  stateRef.current = state

  // Abandon workflow if user stops handling this vehicle
  useEffect(() => {
    if (!isHandledByCurrentUser && !stateRef.current.matches('idle')) {
      send({ type: 'ABANDON' })
    }
  }, [isHandledByCurrentUser, send])

  // Only users who can actually request a hub return should open the
  // progress subscription. In the federated schema it is gated on
  // AD_FLEET_CONTROL_HUB_RETURN, so subscribing without the right would
  // surface a permission error toast on every vehicle selection.
  const isHubReturnEnabled = useUserHasAccess({
    requiredRights: [BoumRight.AD_FLEET_CONTROL_HUB_RETURN],
  })

  // Only track progress once a hub return has actually been requested in this
  // session. Subscribing for the whole selection would open a subscription for
  // every mounted provider; we accept losing progress after a page reload in
  // exchange for not subscribing when nothing is in flight.
  const isExecuting =
    state.matches('executing') || state.matches('awaitingResult')

  const { data: progressData, error: progressError } =
    useErrorHandlingSubscription(
      useAdVehicleStreamHubReturnProgressSubscription,
      {
        variables: { vehicleId: vehicle.id },
        skip: !isHubReturnEnabled || !isExecuting,
      },
    )

  useEffect(() => {
    if (!progressData?.fccHubReturnRequestProgress) return
    const { status: progressStatus } = progressData.fccHubReturnRequestProgress

    switch (progressStatus) {
      case FccHubReturnRequestStatus.Completed:
        send({ type: 'PROGRESS_COMPLETED' })
        break
      case FccHubReturnRequestStatus.Failed:
        send({ type: 'PROGRESS_FAILED' })
        break
    }
  }, [progressData, send])

  // Treat a subscription transport error as a failure only while we're
  // awaiting a result, so the card doesn't hang indefinitely.
  useEffect(() => {
    if (progressError && stateRef.current.matches('awaitingResult')) {
      send({ type: 'PROGRESS_FAILED', reason: 'transportError' })
    }
  }, [progressError, send])

  const requestHubReturn = () => {
    send({ type: 'SHOW_CARD' })
  }

  const confirmHubReturn = (hubId: string) => {
    send({ type: 'EXECUTE', hubId })

    requestHubReturnMutation({
      variables: {
        input: {
          vehicleId: vehicle.id,
          hubId,
        },
      },
      onCompleted: () => {
        send({ type: 'MUTATION_SUCCESS' })
      },
      onError: () => {
        send({ type: 'MUTATION_ERROR' })
      },
    })
  }

  return (
    <HubReturnContext.Provider
      value={{
        activeCard: state.context.activeCard,
        state,
        isExecuting,
        requestHubReturn,
        confirmHubReturn,
        abandon: () => send({ type: 'ABANDON' }),
      }}
    >
      {children}
    </HubReturnContext.Provider>
  )
}
